import dayjs from 'dayjs'

import { DocumentCategory } from 'src/graphql/types/documents'
import { StepType } from 'src/graphql/types/steps'
import { toDayKey } from 'src/utils/date/toMonthYearKey'

export const EXPIRATION_WINDOW_DAYS = 60

export const TRACKED_EXPIRATION_CATEGORIES: DocumentCategory[] = [
  DocumentCategory.CDL,
  DocumentCategory.MVR,
  DocumentCategory.DOT_MEDICAL,
  DocumentCategory.CLEARING_HOUSE,
  DocumentCategory.TWIC_CARD,
]

// StepType equivalents of TRACKED_EXPIRATION_CATEGORIES
// Note: StepType.CLEARINGHOUSE vs DocumentCategory.CLEARING_HOUSE
// Note: TWIC_CARD has no StepType equivalent
export const TRACKED_EXPIRATION_STEP_TYPES: StepType[] = [
  StepType.CDL,
  StepType.MVR,
  StepType.DOT_MEDICAL,
  StepType.CLEARINGHOUSE,
]

export const isCategoryTracked = (category: DocumentCategory): boolean =>
  TRACKED_EXPIRATION_CATEGORIES.includes(category)

export const isStepTypeTracked = (stepType: StepType): boolean =>
  TRACKED_EXPIRATION_STEP_TYPES.includes(stepType)

// Returns the id of the document's ACTIVE expiration tracker, if any.
// A document has at most one ACTIVE tracker at a time (enforced by the
// backend guard in createTrackerForDocument). Terminal-state trackers
// (RENEWED, CANCELLED, ARCHIVED) are ignored — they don't block starting
// tracking again and they shouldn't light up the clock icon.
//
// Requires the caller's GraphQL query to select `status` alongside
// `expirationTrackerId` on `document.expirationTrackers`.
export const getActiveTrackerId = (
  document: {
    expirationTrackers?: { expirationTrackerId: string; status?: string }[]
  }
): string | null => {
  const active = document?.expirationTrackers?.find(
    (tracker) => tracker.status === 'ACTIVE'
  )
  return active?.expirationTrackerId ?? null
}

// Client-side eligibility for the "Track expiration" action on a document.
// Mirrors the backend guards in createTrackerForDocument so the UI hides the
// option whenever the API would reject. The API remains the source of truth.
export const canCreateTrackerForDocument = ({
  document,
  applicant,
  expirationTrackingEnabled,
}: {
  document?: {
    status?: string
    expirationDate?: string | Date | null
    isRenewed?: boolean
    expirationTrackers?: { expirationTrackerId: string; status?: string }[]
  } | null
  applicant?: {
    status?: string
    applicantStage?: string
  } | null
  expirationTrackingEnabled?: boolean | null
}): boolean => {
  if (!expirationTrackingEnabled) return false
  if (
    applicant?.status !== 'ACTIVE' ||
    applicant?.applicantStage !== 'HIRED'
  ) {
    return false
  }
  if (document?.status !== 'ACTIVE') return false
  if (!document?.expirationDate) return false
  if (document?.isRenewed) return false
  if (getActiveTrackerId(document)) return false
  return true
}

export type ExpirationStatus = 'expired' | 'missing' | 'expiring' | 'healthy'

export interface ExpirationLabel {
  status: ExpirationStatus
  color: string
  label: string
  daysRemaining: number | null
}

export const getExpirationLabel = (
  expirationDate: string | Date | null,
  isTrackedCategory: boolean,
  windowDays: number = EXPIRATION_WINDOW_DAYS
): ExpirationLabel => {
  if (!expirationDate) {
    if (isTrackedCategory) {
      return {
        status: 'missing',
        color: 'red',
        label: 'Missing expiration date',
        daysRemaining: null,
      }
    }
    return { status: 'healthy', color: 'green', label: '', daysRemaining: null }
  }

  const today = dayjs().startOf('day')
  const expDate = dayjs(toDayKey(expirationDate)).startOf('day')
  const daysRemaining = expDate.diff(today, 'day')

  if (daysRemaining < 0) {
    const absDays = Math.abs(daysRemaining)
    return {
      status: 'expired',
      color: 'red',
      label: absDays === 1 ? 'Expired 1 day ago' : `Expired ${absDays} days ago`,
      daysRemaining,
    }
  }

  if (daysRemaining <= windowDays) {
    return {
      status: 'expiring',
      color: 'yellow',
      label:
        daysRemaining === 0
          ? 'Expires today'
          : daysRemaining === 1
            ? 'Expires in 1 day'
            : `Expires in ${daysRemaining} days`,
      daysRemaining,
    }
  }

  return {
    status: 'healthy',
    color: 'green',
    label: `Expires in ${daysRemaining} days`,
    daysRemaining,
  }
}
